import { createUserAssociation, Transaction } from "deso-protocol";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, ButtonGroup, Col, Collapse, Container, Dropdown, Form, FormControl, InputGroup, Modal, Row, Spinner, ToggleButton } from "react-bootstrap";
import { QRCode } from 'react-qrcode-logo';
import { localFees, systemKey } from "..";
import { deso_graphql } from "../utils/graphql";

export const LinkQRCode = ({ data = null, currentUser = null, setQRParameters = undefined, preview = false }) => {
  const qrRef = useRef(null);
  const fileInputRef = useRef();
  const size = 512;

  const defaultParams = {
    value: !preview ? data || "example text, example text, example text, example text, example text" : "example text, example text, example text, example text, example text",
    ecLevel: "H",
    enableCORS: true,
    size: size,
    quietZone: 10,
    bgColor: "transparent",
    bgColorReserved: "#000000",
    fgColor: "#FFFFFF",
    logoImage: "",
    logoWidth: 60,
    logoHeight: 60,
    logoOpacity: 1,
    removeQrCodeBehindLogo: true,
    logoPadding: 0,
    logoPaddingStyle: "circle",
    eyeRadius: [
        {
            outer: [0, 0, 0, 0],
            inner: [0, 0, 0, 0],
        },
        {
            outer: [0, 0, 0, 0],
            inner: [0, 0, 0, 0],
        },
        {
            outer: [0, 0, 0, 0],
            inner: [0, 0, 0, 0],
        }
    ],
    eyeColor: [
        {
            outer: '#FFFFFF',
            inner: '#FFFFFF'
        },
        {
            outer: '#FFFFFF',
            inner: '#FFFFFF'
        },
        {
            outer: '#FFFFFF',
            inner: '#FFFFFF'
        },
    ],
    qrStyle: "fluid",
  };
  
  const [qrParameters, setQrParameters] = useState(defaultParams);
  const [tempQrParameters, setTempQrParameters] = useState(defaultParams);
  const [mode, setMode] = useState("simple");
  const [imageType, setImageType] = useState("Avatar");
  const [sourceImage, setSourceImage] = useState(null);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const variables = {
          first: 1, // We only need to check if one exists
          offset: 0,
          condition: {
            associationType: "QR_DEFAULT_STYLE",
            associationValue: "QR_DEFAULT_STYLE",
          },
        };
        const data = await deso_graphql({
                operationName: "GetDefaultPreferences",
                variables,
                query: `
                  query GetDefaultPreferences($first: Int, $offset: Int, $condition: UserAssociationCondition) {
                    userAssociations(first: $first, offset: $offset, condition: $condition) {
                      nodes {
                        associationValue
                        extraData
                      }
                    }
                  }`,
              });

        if (data?.data?.userAssociations?.nodes?.[0]?.extraData?.qrParameters) {
          const parsedParams = JSON.parse(data?.data?.userAssociations?.nodes?.[0]?.extraData?.qrParameters);
          const { value, ...paramsWithoutValue } = parsedParams;
          const finalParams = {
            ...defaultParams,
            ...paramsWithoutValue,
          };
          setQrParameters(finalParams);
          setTempQrParameters(finalParams);
          if(setQRParameters) {
            setQRParameters(finalParams);
          }
        } else {
          console.log("No user preferences found.");
        }
      } catch (error) {
        console.error("Failed to fetch user preferences:", error);
      }
    };

    fetchPreferences();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setQrParameters((prev) => ({
        ...prev,
        ...tempQrParameters,
      }));
      if(setQRParameters) {
        setQRParameters((prev) => ({
          ...prev,
          ...tempQrParameters,
        }));
      }
    }, 50);
  
    return () => clearTimeout(debounceTimer);
  }, [tempQrParameters]);

  // Fetch default avatar/logo from user
  useEffect(() => {
    const fetchLogo = async () => {
      const response = await fetch(
        `https://node.deso.org/api/v0/get-single-profile-picture/${currentUser?.PublicKeyBase58Check}`,
        { mode: "cors" }
      );
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempQrParameters((prev) => ({ ...prev, logoImage: reader.result }));
      };
      reader.readAsDataURL(blob);
    };

    if (currentUser?.PublicKeyBase58Check && imageType === "Avatar") {
      fetchLogo();
    } else if (imageType === "Custom") {
      setTempQrParameters((prev) => ({ ...prev, logoImage: "" }));
    } else if (imageType === "None") {
      setTempQrParameters((prev) => ({ ...prev, logoImage: "" }));
    }
  }, [imageType, currentUser?.PublicKeyBase58Check]);

  const handleSaveQR = () => {
    if (qrRef.current && typeof qrRef.current.download === "function") {
      qrRef.current.download("png", "qrcode.png");
    }
  };

  const updateEyeColor = (eyeIndex, eyeType, value) => {
    setTempQrParameters((prev) => {
      const updatedEyeColor = [...prev.eyeColor]; // Clone the existing eyeColor array
      
      updatedEyeColor[eyeIndex] = {
        ...updatedEyeColor[eyeIndex], // Copy the object at the eyeIndex
        [eyeType]: value, // Update the outer or inner color
      };
  
      return { ...prev, eyeColor: updatedEyeColor }; // Return the updated state
    });
  };
  
  

  const updateEyeRadius = (eyeIndex, eyeType, radiusIndex, value) => {
    // eyeIndex: 0 for topLeft, 1 for topRight, 2 for bottomLeft
    // eyeType: 'outer' or 'inner'
    // radiusIndex: index of the specific radius (0, 1, 2, or 3)
    // value: new radius value
  
    setTempQrParameters((prev) => {
      const updatedEyeRadius = [...prev.eyeRadius]; // Clone the existing eyeRadius array
      updatedEyeRadius[eyeIndex] = {
        ...updatedEyeRadius[eyeIndex], // Copy the object at the eyeIndex
        [eyeType]: updatedEyeRadius[eyeIndex][eyeType].map((radius, i) => {
          return i === radiusIndex ? value : radius; // Update only the specific radius
        }),
      };
      
      return { ...prev, eyeRadius: updatedEyeRadius }; // Return the updated state
    });
  };
  
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const image = new Image();
      image.onload = () => {
        const maxSize = size * 0.25;
        let width = image.width;
        let height = image.height;

        if (width > maxSize || height > maxSize) {
          const aspectRatio = width / height;
          if (width > height) {
            width = maxSize;
            height = Math.round(width / aspectRatio);
          } else {
            height = maxSize;
            width = Math.round(height * aspectRatio);
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, width, height);
        const resizedData = canvas.toDataURL("image/png");

        // Save resized image as the source image for future cropping
        setSourceImage(resizedData);
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  };  
  
  useEffect(() => {
    if (!sourceImage) return;
  
    const image = new Image();
    image.onload = () => {
      const width = image.width;
      const height = image.height;
  
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
  
      if (tempQrParameters.logoPaddingStyle === "circle") {
        const radius = Math.min(width, height) / 2;
        ctx.beginPath();
        ctx.arc(width / 2, height / 2, radius, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();
      }
      // If "square", no clipping needed — just draw normally
  
      ctx.drawImage(image, 0, 0, width, height);
  
      const finalImage = canvas.toDataURL("image/webp", 0.7);
      setTempQrParameters((prev) => ({
        ...prev,
        logoImage: finalImage,
      }));
    };
    image.src = sourceImage;
  }, [sourceImage, qrParameters.logoPaddingStyle]);

  const corners = ["Top Left", "Top Right", "Bottom Right", "Bottom Left"];
  const cornerIcons = ["bi-arrow-up-left-square", "bi-arrow-up-right-square", "bi-arrow-down-right-square", "bi-arrow-down-left-square"];

  const [savingDefault, setSavingDefault] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSaveQRDefaults = async () => {
    setSavingDefault(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {

      const { value, ...qrParametersWithoutValue } = qrParameters;
      const associationRequest = {
        AppPublicKeyBase58Check: systemKey,
        TargetUserPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
        AssociationType: "QR_DEFAULT_STYLE",
        AssociationValue: "QR_DEFAULT_STYLE",
        ExtraData: {
          qrParameters: JSON.stringify(qrParametersWithoutValue),
          timeStamp: Date.now(),
        },
        TransactionFees: [localFees()],
      };
      
      console.log("Create Association:",associationRequest);
      const associationResponse = await createUserAssociation(associationRequest);
      console.log("Association:",associationRequest,associationResponse);

      // Step 3: Check if the creation was successful and update the UI
      if (associationResponse?.submittedTransactionResponse) {
        setSuccessMessage("Defaults Saved");
      } else {
        console.error("Failed to create short link on blockchain:", associationResponse?.error);
        setErrorMessage(`Failed to save default prefernences, please try again`);
      }
    } catch (error) {
      console.error("Error saving QR defaults:", error);
    } finally {
      setSavingDefault(false);
    }
  }

  console.log("qrParameters", qrParameters);
  return (
    <div className="container text-center">
      <Row className="justify-content-center mt-3 align-items-center">
        <Col xs={12} md={5} className="my-1">
            {qrParameters && (
                <div className="ratio ratio-1x1">
                    <QRCode
                        ref={qrRef}
                        style={{ maxWidth: "100%", height: "auto" }}
                        value={qrParameters.value}
                        ecLevel={qrParameters.ecLevel}
                        enableCORS={qrParameters.enableCORS}
                        size={qrParameters.size}
                        quietZone={qrParameters.quietZone}
                        bgColor={qrParameters.bgColor}
                        fgColor={qrParameters.fgColor}
                        logoImage={qrParameters.logoImage ? qrParameters.logoImage : null}
                        logoWidth={qrParameters.logoImage ? qrParameters.logoWidth : null}
                        logoHeight={qrParameters.logoImage ? qrParameters.logoHeight : null}
                        logoOpacity={qrParameters.logoImage ? qrParameters.logoOpacity : null}
                        removeQrCodeBehindLogo={qrParameters.logoImage ? qrParameters.removeQrCodeBehindLogo : null}
                        logoPadding={qrParameters.logoImage ? qrParameters.logoPadding : null}
                        logoPaddingStyle={qrParameters.logoImage ? qrParameters.logoPaddingStyle : null}
                        eyeRadius={qrParameters.eyeRadius}
                        eyeColor={qrParameters.eyeColor}
                        qrStyle={qrParameters.qrStyle}
                    />
                </div>
            )}
            {preview && (
                <div className="text-muted small">
                <span className="fw-bold">Preview Only</span> - generate for actual QR code
                </div>
            )}
        </Col>

        <Col xs={12} md={7} className="align-items-center my-1 small">
          <Row>
            <Col xs={12} className="d-flex justify-content-between align-items-center px-1 mb-2">
              <span className="fw-bold me-2"><i className="bi bi-sliders me-2"></i>Editor</span>
              <ButtonGroup className="flex-fill">
                {["simple", "advanced"].map((style) => (
                  <ToggleButton
                    key={style}
                    type="radio"
                    variant="outline-secondary"
                    size="sm"
                    name="mode"
                    value={mode}
                    active={mode === style}
                    onClick={() => setMode(style)}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Col>
          </Row>

          
          <Row>
            <Col xs={12} className="mt-2 d-flex align-items-center px-1 fw-bold">
                <i className="bi bi-palette-fill me-2"></i>Palette
            </Col>
            <Col xs={6} className="d-flex justify-content-between align-items-center mt-2 px-1">
              <Form.Label className="my-0">Background</Form.Label>
              <Form.Check
                type="switch"
                className=""
                checked={tempQrParameters.bgColor !== "transparent"}
                onChange={() =>
                  setTempQrParameters((prev) => ({
                    ...prev,
                    bgColor: prev.bgColor === "transparent" ? prev.bgColorReserved : "transparent",
                    bgColorReserved: prev.bgColor === "transparent" ? prev.bgColorReserved : prev.bgColor
                  }))
                }
              />
              <Form.Control
                type="color"
                value={tempQrParameters.bgColor === "transparent" ? tempQrParameters.bgColorReserved : tempQrParameters.bgColor}
                disabled={tempQrParameters.bgColor === "transparent"}
                onChange={(e) => setTempQrParameters((prev) => ({ ...prev, bgColor: e.target.value }))}
              />
            </Col>
            <Col xs={6} className="d-flex justify-content-between align-items-center mt-2 px-1 ps-2">
              <Form.Label className="my-0">Data</Form.Label>
              <Form.Control
                type="color"
                value={tempQrParameters.fgColor}
                onChange={(e) => setTempQrParameters((prev) => ({ ...prev, fgColor: e.target.value }))}
              />
            </Col>
            <Col xs={6} className="d-flex justify-content-between align-items-center mt-2 px-1">
              {mode === "simple" && (
                <>
                  <Form.Label className="my-0">Eyes</Form.Label>
                  <Form.Control
                    type="color"
                    value={tempQrParameters.eyeColor[0].outer}
                    onChange={(e) => {
                        const color = e.target.value;
                        setTempQrParameters((prev) => ({
                          ...prev,
                          eyeColor: prev.eyeColor.map(item => ({
                            ...item, // keep other properties of each eyeColor item
                            outer: color, // Update outer value
                            inner: color  // Update inner value
                          }))
                        }));
                      }}
                  />
                </>
              )}
            </Col>
          </Row>

          <Row className="mt-1">
            <Col xs={12} className="mt-2 d-flex align-items-center px-1 fw-bold">
                <i className="bi bi-image me-2"></i>Image
            </Col>
            <Col xs={12} className="mt-2 d-flex align-items-center px-1">
              <ButtonGroup className="flex-fill">
                {["None","Avatar","Custom"].map((style) => (
                  <ToggleButton
                    key={style}
                    type="radio"
                    variant="outline-secondary"
                    size="sm"
                    name="mode"
                    value={imageType}
                    checked={imageType === style}
                    onClick={() => setImageType(style)}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Col>
            {imageType !== "None" && (
              <>
                {imageType === `Custom` && (
                  <Col xs={12} className="mt-2 px-1 d-flex align-items-center">
                    <Form.Label className="text-start m-0" style={{ width: "42%" }}>Upload</Form.Label>
                    <Form.Control ref={fileInputRef} size="sm" className="visually-hidden flex-fill" type="file" accept="image/*" onChange={handleImageUpload} />
                    <Button onClick={triggerFileInput} size="sm" className="w-100"><i className="bi bi-upload me-2"></i>Upload image</Button>
                  </Col>
                )}
                {mode === "advanced" && (
                  <>
                    <Col xs={12} className="mt-2 d-flex align-items-center px-1">
                      <Form.Label className="text-start m-0" style={{ width: "42%" }}>Shape</Form.Label>
                      <ButtonGroup className="flex-fill w-100">
                        {["square","circle"].map((style) => (
                          <ToggleButton
                            key={style}
                            type="radio"
                            variant="outline-secondary"
                            size="sm"
                            name="imagePaddingStyle"
                            value={tempQrParameters.logoPaddingStyle}
                            checked={tempQrParameters.logoPaddingStyle === style}
                            onClick={() => setTempQrParameters((prev) => ({ ...prev, logoPaddingStyle: style }))}
                          >
                            {style.charAt(0).toUpperCase() + style.slice(1)}
                          </ToggleButton>
                        ))}
                      </ButtonGroup>
                    </Col>
                    <Col xs={12} className="mt-2 d-flex align-items-center px-1">
                      <Form.Label className="text-start m-0" style={{ width: "42%" }}>Opacity</Form.Label>
                      <Form.Range
                        min={0}
                        max={1}
                        step={0.05}
                        value={tempQrParameters.logoOpacity}
                        onChange={(e) => setTempQrParameters((prev) => ({ ...prev, logoOpacity: parseFloat(e.target.value) }))}
                      />
                    </Col>
                    <Col xs={12} className="mt-2 d-flex align-items-center px-1">
                      <Form.Label className="text-start m-0" style={{ width: "42%" }}>Size</Form.Label>
                      <Form.Range
                        min={0}
                        max={Math.round(size * 0.25)}
                        step={1}
                        value={tempQrParameters.logoWidth}
                        onChange={(e) => setTempQrParameters((prev) => ({ ...prev, logoWidth: parseInt(e.target.value), logoHeight: parseInt(e.target.value) }))}
                      />
                    </Col>
                    <Col xs={12} className="mt-2 d-flex align-items-center px-1">
                      <Form.Label className="text-start m-0" style={{ width: "42%" }}>Background</Form.Label>
                      <ButtonGroup className="flex-fill w-100">
                        {[false,true].map((style) => (
                          <ToggleButton
                            key={style}
                            type="radio"
                            variant="outline-secondary"
                            size="sm"
                            name="imagePaddingStyle"
                            value={tempQrParameters.removeQrCodeBehindLogo}
                            checked={tempQrParameters.removeQrCodeBehindLogo === style}
                            onClick={() => setTempQrParameters((prev) => ({ ...prev, removeQrCodeBehindLogo: style }))}
                          >
                            {style ? "Background Fill" : "Transparent"}
                          </ToggleButton>
                        ))}
                      </ButtonGroup>
                    </Col>
                    {tempQrParameters.removeQrCodeBehindLogo && (
                      <Col xs={12} className="mt-2 d-flex align-items-center px-1">
                        <Form.Label className="text-start m-0" style={{ width: "42%" }}>Padding</Form.Label>
                        <Form.Range
                          min={0}
                          max={Math.round(tempQrParameters.logoWidth * 0.025)}
                          step={1}
                          value={tempQrParameters.logoPadding}
                          onChange={(e) => setTempQrParameters((prev) => ({ ...prev, logoPadding: parseInt(e.target.value) }))}
                        />
                      </Col>
                    )}
                  </>
                )}
              </>
            )}
          </Row>

          <Row className="mt-3">
            <Col xs={12} className="text-start fw-bold mb-2 px-1 fw-bold">
              <i className="bi bi-qr-code me-2"></i>QR Styling
            </Col>
            <Col xs={12} className="d-flex align-items-center px-1">
              <Form.Label className="mb-0 me-2 visually-hidden">Data</Form.Label>
              <ButtonGroup className="flex-fill">
                {["square", "dots", "fluid"].map((style) => (
                  <ToggleButton
                    key={style}
                    type="radio"
                    variant="outline-secondary"
                    size="sm"
                    name="qrStyle"
                    value={tempQrParameters.qrStyle}
                    checked={tempQrParameters.qrStyle === style}
                    onClick={() => setTempQrParameters((prev) => ({ ...prev, qrStyle: style }))}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Col>
            <Col xs={12} className="mt-2 d-flex align-items-center px-1">
              <Form.Label className="text-start m-0" style={{ width: "42%" }}>Padding</Form.Label>
              <Form.Range
                min={0}
                max={40}
                step={1}
                value={tempQrParameters.quietZone}
                onChange={(e) => setTempQrParameters((prev) => ({ ...prev, quietZone: parseInt(e.target.value) }))}
              />
            </Col>
          </Row>

          <Row className="mt-2">
            {mode === "simple" ? (
              <Col xs={12} className="mt-2 d-flex align-items-center px-1">
                <Form.Label className="text-start mb-0" style={{ width: "42%" }}>Eye Radius</Form.Label>
                <Form.Range
                  min={0}
                  max={100}
                  step={1}
                  value={tempQrParameters.eyeRadius[0].outer[0]} // Use the first outer radius as the value
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setTempQrParameters((prev) => ({
                      ...prev,
                      eyeRadius: prev.eyeRadius.map((eye) => ({
                        ...eye,
                        outer: eye.outer.map(() => newValue),
                        inner: eye.outer.map(() => newValue),
                      })),
                    }));
                  }}
                />
              </Col>
            ) : (
              <>
              {["Left", "Right", "Bottom"].map((eyeLabel, idx) => (
                    <div key={eyeLabel}>
                        <Row className="align-items-center">
                            <Col xs={12} className="text-start fw-bold my-2 px-1">{eyeLabel.replace(/([A-Z])/g, " $1")} Eye</Col>
                            <Col xs={3} className="d-flex flex-column align-items-center justify-content-center px-1">
                                <Form.Label className="small mb-0">Outer</Form.Label>
                                <Form.Control
                                type="color"
                                value={tempQrParameters.eyeColor[idx].outer} // Access the correct value here
                                onChange={(e) => updateEyeColor(idx, "outer", e.target.value)} // Update the correct value
                                />
                            </Col>
                            <Col xs={9}>
                              <Row>
                                {tempQrParameters.eyeRadius[idx].outer.map((r, i) => (
                                    <Col xs={6} className={`d-flex px-1 align-items-center justify-content-between ${i<2 ? `mb-1` : ``} w-50`} key={eyeLabel+'radius'+i}>
                                        <Form.Label className="mb-0" style={{ width: "22%" }}><i className={`bi ${cornerIcons[i]}`}></i></Form.Label>
                                        <Form.Range
                                            min={0}
                                            max={25}
                                            step={1}
                                            value={r}
                                            style={{ width: "78%" }}
                                            onChange={(e) => updateEyeRadius(idx, "outer", i, parseInt(e.target.value))}
                                        />
                                    </Col>
                                ))}
                                </Row>
                            </Col>
                        </Row>
                        <Row className="align-items-center my-3">
                            <Col xs={3} className="d-flex flex-column align-items-center justify-content-center">
                                <Form.Label className="small mb-0">Inner</Form.Label>
                                <Form.Control
                                type="color"
                                value={tempQrParameters.eyeColor[idx].inner} // Access the correct value here
                                onChange={(e) => updateEyeColor(idx, "inner", e.target.value)} // Update the correct value
                                />
                            </Col>
                            <Col xs={9}>
                              <Row>
                                {tempQrParameters.eyeRadius[idx].inner.map((r, i) => (
                                    <Col xs={6} className={`px-1 d-flex align-items-center justify-content-between ${i<2 ? `mb-1` : ``} w-50`} key={eyeLabel+'radius'+i}>
                                        <Form.Label className="mb-0" style={{ width: "22%" }}><i className={`bi ${cornerIcons[i]}`}></i></Form.Label>
                                        <Form.Range
                                            min={0}
                                            max={25}
                                            step={1}
                                            value={r}
                                            style={{ width: "78%" }}
                                            onChange={(e) => updateEyeRadius(idx, "inner", i, parseInt(e.target.value))}
                                        />
                                    </Col>
                                ))}
                                </Row>
                            </Col>
                        </Row>
                    </div>
                ))}
              </>
            )}
          </Row>

          {!preview && (
            <>
              <Button className="mt-3 mx-2" onClick={handleSaveQR}>
                <i className="bi bi-floppy-fill me-2" /> Save QR Code
              </Button>
            </>
          )}
          <Button className="mt-3 mx-2" disabled={savingDefault} onClick={handleSaveQRDefaults}>
            {savingDefault ? ( 
              <><Spinner /> saving...</>
            ) : (
              <><i className="bi bi-palette-fill me-2" /> Save as default style</>
            )}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
